<template>
  <div
    class="amazon-left"
    style="display: flex; flex-direction: column; height: 100%"
  >
    <div class="block" style="flex: 1; display: flex; flex-direction: column">
      <label class="block-label">上传国内尺码图</label>
      <div
        class="modern-upload"
        style="
          flex: 1;
          min-height: 280px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
        "
        @click="$refs.fileInput.click()"
        @dragover.prevent
        @drop.prevent="onDrop"
      >
        <input
          ref="fileInput"
          type="file"
          class="file-input-hidden"
          accept="image/*"
          @change="onFileChange"
        />

        <div v-if="!workbench.sizeChart.preview" class="upload-visual">
          <div class="upload-glow"></div>
          <div class="upload-icon-bubble">
            <svg
              class="upload-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 4v16m8-8H4"
              />
            </svg>
          </div>
          <div class="upload-copy">
            <div class="upload-title">点击或拖拽上传图片</div>
            <div class="upload-sub">支持 JPG / PNG / WEBP</div>
          </div>
        </div>

        <div
          v-else
          class="upload-visual"
          style="
            border: none;
            background: transparent;
            width: 100%;
            height: 100%;
          "
        >
          <img
            :src="workbench.sizeChart.preview"
            alt="preview"
            style="
              width: 100%;
              height: 100%;
              object-fit: contain;
              border-radius: 12px;
            "
          />
          <button
            class="clear-pill"
            style="
              position: absolute;
              bottom: 12px;
              right: 12px;
              box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
            "
            @click.stop="clearImage"
          >
            重新上传
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="workbench.sizeChart.bgImage"
      class="block"
      style="margin-top: 16px"
    >
      <label
        class="block-label"
        style="display: flex; justify-content: space-between"
      >
        <span>防扫描底图 (来自宣传图)</span>
        <span
          style="
            color: #ef4444;
            cursor: pointer;
            font-size: 12px;
            font-weight: normal;
          "
          @click="workbench.sizeChart.bgImage = ''"
          >清除底图</span
        >
      </label>
      <div
        class="upload-visual"
        style="
          height: 140px;
          border: 2px solid #10b981;
          padding: 0;
          position: relative;
          overflow: hidden;
        "
      >
        <img
          :src="workbench.sizeChart.bgImage"
          alt="bgPreview"
          style="width: 100%; height: 100%; object-fit: cover"
        />
        <div
          style="
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            background: rgba(16, 185, 129, 0.85);
            color: #fff;
            font-size: 11px;
            padding: 6px;
            text-align: center;
          "
        >
          合成时，尺码图将以 50% 透明度叠加在此图下方
        </div>
      </div>
    </div>
    <div
      style="
        margin-top: 20px;
        padding: 16px;
        background: #f8fafc;
        border-radius: 8px;
        border: 1px solid #e2e8f0;
      "
    >
      <h4
        style="
          margin: 0 0 10px;
          font-size: 13px;
          font-weight: 600;
          color: #334155;
          display: flex;
          align-items: center;
          gap: 6px;
        "
      >
        <svg
          style="width: 16px; height: 16px; color: var(--primary-color)"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          ></path>
        </svg>
        后台自动处理规则
      </h4>
      <ul
        style="
          margin: 0;
          padding-left: 20px;
          font-size: 12px;
          color: #64748b;
          line-height: 1.8;
        "
      >
        <li>
          自动将 <strong>厘米(cm)</strong> 换算为美国标准
          <strong>英寸(inch)</strong>
        </li>
        <li>
          自动将 <strong>斤</strong> 换算为美国标准 <strong>磅(lb)</strong>
        </li>
        <li>翻译全部中文标签，输出美区习惯的英文尺码表</li>
        <li>底部自动嵌入英文版<strong>“防退货温馨提示”</strong></li>
      </ul>
    </div>

    <button
      class="primary-btn full"
      style="margin-top: 20px; height: 44px; font-size: 14px"
      :disabled="
        workbench.sizeChart.isExtracting || !workbench.sizeChart.preview
      "
      @click="handleExtract"
    >
      {{
        workbench.sizeChart.isExtracting
          ? "AI 正在提取并翻译数据中..."
          : "提取数据与中英转换"
      }}
    </button>
  </div>
</template>

<script setup>
import { ref } from "vue";
import { useWorkbenchStore } from "@/stores/useWorkbenchStore";

const workbench = useWorkbenchStore();
const fileInput = ref(null);

const onFileChange = (e) => {
  const file = e.target.files[0];
  if (file) workbench.setSizeChartFile(file);
};

const onDrop = (e) => {
  const file = e.dataTransfer.files[0];
  if (file && file.type.startsWith("image/")) {
    workbench.setSizeChartFile(file);
  }
};

const clearImage = () => {
  workbench.sizeChart.preview = "";
  workbench.sizeChart.image = "";
  if (fileInput.value) fileInput.value.value = "";
};

const handleExtract = async () => {
  try {
    await workbench.extractSizeData();
  } catch (error) {
    alert(error.message);
  }
};
</script>
